import { useSiblingReorder } from '../../components/useSiblingReorder.js';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import type { ReportSummary } from '../../api/reports.js';
import { RowMenu, type MenuItem } from './ReportsFolderTree.js';
import {
  ChevronRight as Chevron,
  FolderIcon,
  ReportIcon as ReportLeafIcon,
} from '../../components/icons.js';

/** CSAGE-192: reports list page size. */
const PAGE_SIZE = 20;

/** Prev/next pager shown under a folder's list when it spans multiple pages. */
function Pager({
  page,
  pages,
  onPage,
}: {
  page: number;
  pages: number;
  onPage: (p: number) => void;
}): JSX.Element | null {
  if (pages <= 1) return null;
  return (
    <div className="folder-tree-pager">
      <button
        type="button"
        className="folder-tree-pager-btn"
        disabled={page <= 0}
        onClick={() => onPage(page - 1)}
        aria-label="Previous page"
      >
        ‹
      </button>
      <span className="folder-tree-pager-info">
        Page {page + 1} of {pages}
      </span>
      <button
        type="button"
        className="folder-tree-pager-btn"
        disabled={page >= pages - 1}
        onClick={() => onPage(page + 1)}
        aria-label="Next page"
      >
        ›
      </button>
    </div>
  );
}

/**
 * Consumer (embed) reports list — two folders, matching the legacy Canopy
 * Reports module:
 *
 *   Reports      the base report *definitions* (Uptime, Offline Devices, …),
 *                one row per type. Everyone sees these; running one is how you
 *                get a report. The Jasper→Sage migration created a copy per
 *                (location × period), but those copies do NOT belong here —
 *                only the base definitions do.
 *   My Reports   the copies THIS user made / owns (visibility 'personal').
 *                Empty for a user who hasn't copied any, exactly like legacy's
 *                "Modified Reports".
 *
 * Split is by `report.visibility`: 'personal' → My Reports (a user's copy),
 * everything else → the shared base definitions in Reports. Pure presentation
 * over the reports the API already returns — no new data path.
 */
export interface Definition {
  key: string;
  label: string;
  /** The instance opened when the base definition is run. */
  representativeId: string;
  count: number;
}

function parseReportName(name: string): { type: string; hasLocation: boolean } {
  let rest = name;
  let hasLocation = false;
  // Migration names copies with an em dash: "Bentley — Uptime Report (…)".
  const dash = name.indexOf('—');
  if (dash >= 0) {
    hasLocation = true;
    rest = name.slice(dash + 1).trim();
  }
  const m = rest.match(/\(([^)]+)\)\s*$/u);
  if (m && m.index !== undefined) rest = rest.slice(0, m.index).trim();
  return { type: rest || name, hasLocation };
}

/** Collapse instances to one definition per report NAME.
 *
 * Grouping by *name* (not template) is deliberate. The Jasper→Sage migration
 * created a copy of each report per workgroup, and those copies use a different
 * template than the customer-level report — so grouping by template rendered
 * each generation as its own tree row (the "Offline Devices ×2" duplicates).
 * Legacy Jasper shows one row per report type, run with parameters; matching
 * that means one row per name. The run target prefers the whole-customer base
 * instance (no location prefix), so "Uptime" runs the customer-level report
 * rather than an arbitrary workgroup copy.
 */
export function buildDefinitions(reports: ReadonlyArray<ReportSummary>): Definition[] {
  const byName = new Map<string, { label: string; base: string | null; first: string }>();
  const counts = new Map<string, number>();
  for (const r of reports) {
    const { type, hasLocation } = parseReportName(r.name);
    const key = type.toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
    let d = byName.get(key);
    if (!d) {
      d = { label: type, base: null, first: r.id };
      byName.set(key, d);
    }
    if (type.length < d.label.length) d.label = type;
    if (!hasLocation && d.base === null) d.base = r.id;
  }
  const defs: Definition[] = [...byName.entries()].map(([key, d]) => ({
    key,
    label: d.label,
    representativeId: d.base ?? d.first,
    count: counts.get(key) ?? 1,
  }));
  // Preserve the API’s persisted order using each definition’s run target.
  const position = new Map(reports.map((r, i) => [r.id, i]));
  defs.sort((a, b) => position.get(a.representativeId)! - position.get(b.representativeId)!);
  return defs;
}

interface Props {
  onSelectSection?: ((folder: string, itemIds: string[]) => void) | undefined;
  onReorderItems?: ((ids: string[]) => Promise<unknown>) | undefined;
  reports: ReadonlyArray<ReportSummary>;
  selectedReportId: string | null;
  onSelectReport(id: string): void;
  searchQuery: string;
  /**
   * CSAGE-353: row actions for My Reports. A user who saves a copy has to be
   * able to curate it — without these the section was read-only and a saved
   * report could never be renamed or removed. Optional so surfaces that render
   * the list without edit rights simply get no kebab.
   */
  onRenameReport?(report: ReportSummary): void;
  onDeleteReport?(report: ReportSummary): void;
  /**
   * CSAGE-353: subscribe straight from the row. The legacy module had this as a
   * calendar icon on each Modified Reports row, and it is the natural next step
   * after saving a copy — the whole point of keeping one is to have it delivered.
   */
  onSubscribeReport?(report: ReportSummary): void;
}

export function ConsumerReportsList({
  reports,
  selectedReportId,
  onSelectReport,
  searchQuery,
  onRenameReport,
  onDeleteReport,
  onSubscribeReport,
  onReorderItems,
  onSelectSection,
}: Props): JSX.Element {
  const reorder = useSiblingReorder(onReorderItems);
  const q = searchQuery.trim().toLowerCase();
  const mine = useMemo(() => reports.filter((r) => r.visibility === 'personal'), [reports]);
  // Subscription-target instances: customer-scoped (hierarchy) clones that exist
  // only because a subscription is scheduled off them (e.g. Mood Media's "Device
  // Summary"). In Jasper these are invisible in browse — Sage surfaces them, but
  // in their own "Scheduled Reports" folder so the main Reports list still matches
  // Jasper's browsable catalog. (CSAGE-272)
  const scheduled = useMemo(
    () => reports.filter((r) => r.visibility === 'hierarchy' && r.scheduleId != null),
    [reports],
  );
  const scheduledIds = useMemo(() => new Set(scheduled.map((r) => r.id)), [scheduled]);
  // The main catalog list = everything shared, minus the subscription-target
  // instances (which move to their own folder) and personal reports.
  const catalog = useMemo(
    () => reports.filter((r) => r.visibility !== 'personal' && !scheduledIds.has(r.id)),
    [reports, scheduledIds],
  );
  const defs = useMemo(() => buildDefinitions(catalog), [catalog]);

  const [foldersOpen, setFoldersOpen] = useState({
    reports: true,
    scheduled: true,
    mine: true,
  });

  const visibleDefs = useMemo(
    () => (!q ? defs : defs.filter((d) => d.label.toLowerCase().includes(q))),
    [defs, q],
  );
  const visibleMine = useMemo(
    () => (!q ? mine : mine.filter((r) => r.name.toLowerCase().includes(q))),
    [mine, q],
  );
  const visibleScheduled = useMemo(
    () => (!q ? scheduled : scheduled.filter((r) => r.name.toLowerCase().includes(q))),
    [scheduled, q],
  );

  const mineOpen = q ? true : foldersOpen.mine;
  const scheduledOpen = q ? true : foldersOpen.scheduled;

  // CSAGE-192: paginate the flat leaf lists (20/page) instead of one endless
  // scroll. Client-side over the already-fetched, definition-collapsed set —
  // reset to page 1 whenever the filter or the underlying set changes.
  const [reportsPage, setReportsPage] = useState(0);
  const [minePage, setMinePage] = useState(0);
  const [scheduledPage, setScheduledPage] = useState(0);
  useEffect(() => setReportsPage(0), [q, visibleDefs.length]);
  useEffect(() => setMinePage(0), [q, visibleMine.length]);
  useEffect(() => setScheduledPage(0), [q, visibleScheduled.length]);
  const reportsPages = Math.max(1, Math.ceil(visibleDefs.length / PAGE_SIZE));
  const minePages = Math.max(1, Math.ceil(visibleMine.length / PAGE_SIZE));
  const scheduledPages = Math.max(1, Math.ceil(visibleScheduled.length / PAGE_SIZE));
  const pagedDefs = visibleDefs.slice(reportsPage * PAGE_SIZE, (reportsPage + 1) * PAGE_SIZE);
  const pagedMine = visibleMine.slice(minePage * PAGE_SIZE, (minePage + 1) * PAGE_SIZE);
  const pagedScheduled = visibleScheduled.slice(
    scheduledPage * PAGE_SIZE,
    (scheduledPage + 1) * PAGE_SIZE,
  );

  return (
    <div className="folder-tree" role="tree" aria-label="Reports" aria-busy={reorder.pending}>
      {reorder.message && (
        <p className="navigation-order-status" role="status">
          {reorder.message}
        </p>
      )}
      {/* The enclosing sidebar section is already titled "Reports" and controls
          this body's visibility, so the base report list renders directly —
          no redundant inner "Reports" folder to expand. "My Reports" and
          "Assigned Reports" below remain distinct sub-groups. */}
      <ul className="folder-tree-list">
        {visibleDefs.length === 0 ? (
          <EmptyRow text="No reports." />
        ) : (
          pagedDefs.map((d) => {
            const isSelected = selectedReportId === d.representativeId;
            return (
              <li
                {...reorder.row(
                  d.representativeId,
                  defs.map((item) => item.representativeId),
                )}
                key={d.key}
                role="treeitem"
                aria-selected={isSelected}
              >
                <button
                  type="button"
                  className={`folder-tree-row folder-tree-row--leaf${
                    isSelected ? ' folder-tree-row--selected' : ''
                  }`}
                  style={{ ...ROW_RESET, paddingLeft: '12px' }}
                  title={d.label}
                  onClick={() => onSelectReport(d.representativeId)}
                >
                  <span className="folder-tree-label">
                    <span className="folder-tree-icon" aria-hidden="true">
                      <ReportLeafIcon />
                    </span>
                    <span className="folder-tree-name">{d.label}</span>
                  </span>
                </button>
              </li>
            );
          })
        )}
      </ul>
      <Pager page={reportsPage} pages={reportsPages} onPage={setReportsPage} />

      {/* Scheduled Reports (CSAGE-272): customer report instances that exist
          only as a subscription target. Hidden when there are none. Grouped
          here so the catalog list above matches Jasper's browsable set while
          these stay visible/runnable. */}
      {scheduled.length > 0 && (
        <>
          <FolderHeader
            onOpenList={() => {
              onSelectSection?.(
                'Scheduled reports',
                scheduled.map((r) => r.id),
              );
              setFoldersOpen((o) => ({ ...o, scheduled: true }));
            }}
            label="Scheduled Reports"
            count={scheduled.length}
            open={scheduledOpen}
            onToggle={() => setFoldersOpen((o) => ({ ...o, scheduled: !o.scheduled }))}
          />
          {scheduledOpen && (
            <>
              <ul className="folder-tree-list">
                {visibleScheduled.length === 0 ? (
                  <EmptyRow text="No matching reports." />
                ) : (
                  pagedScheduled.map((r) => {
                    const isSelected = selectedReportId === r.id;
                    return (
                      <li
                        {...reorder.row(
                          r.id,
                          scheduled.map((item) => item.id),
                        )}
                        key={r.id}
                        role="treeitem"
                        aria-selected={isSelected}
                      >
                        <button
                          type="button"
                          className={`folder-tree-row folder-tree-row--leaf${
                            isSelected ? ' folder-tree-row--selected' : ''
                          }`}
                          style={{ ...ROW_RESET, paddingLeft: '24px' }}
                          title={r.name}
                          onClick={() => onSelectReport(r.id)}
                        >
                          <span className="folder-tree-label">
                            <span className="folder-tree-icon" aria-hidden="true">
                              <ReportLeafIcon />
                            </span>
                            <span className="folder-tree-name">{r.name}</span>
                          </span>
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
              <Pager page={scheduledPage} pages={scheduledPages} onPage={setScheduledPage} />
            </>
          )}
        </>
      )}

      {/* CSAGE-238: hide "My Reports" entirely when the user has no personal
          reports — no empty placeholder section taking up nav space. */}
      {mine.length > 0 && (
        <>
          <FolderHeader
            onOpenList={() => {
              onSelectSection?.(
                'My reports',
                mine.map((r) => r.id),
              );
              setFoldersOpen((o) => ({ ...o, mine: true }));
            }}
            label="My Reports"
            count={mine.length}
            open={mineOpen}
            onToggle={() => setFoldersOpen((o) => ({ ...o, mine: !o.mine }))}
          />
          {mineOpen && (
            <>
              <ul className="folder-tree-list">
                {visibleMine.length === 0 ? (
                  <EmptyRow text="No matching reports." />
                ) : (
                  pagedMine.map((r) => {
                    const isSelected = selectedReportId === r.id;
                    // CSAGE-353: rename/delete for a saved copy. Kebab rather
                    // than inline icons per standards/destructive-actions.md, and
                    // the confirmation names the report per content-style.md §8.
                    const items: MenuItem[] = [
                      { label: 'Open', icon: 'open', action: () => onSelectReport(r.id) },
                      ...(onSubscribeReport
                        ? [
                            {
                              label: 'Subscribe…',
                              icon: 'play' as const,
                              action: () => onSubscribeReport(r),
                            },
                          ]
                        : []),
                      ...(onRenameReport
                        ? [
                            {
                              label: 'Rename…',
                              icon: 'rename' as const,
                              action: () => onRenameReport(r),
                            },
                          ]
                        : []),
                      ...(onDeleteReport
                        ? [
                            {
                              label: 'Delete report',
                              icon: 'delete' as const,
                              destructive: true,
                              action: () => onDeleteReport(r),
                            },
                          ]
                        : []),
                    ];
                    // Row markup mirrors ReportsFolderTree's ReportLeaf exactly —
                    // a flex row wrapping the label button plus the kebab — so the
                    // two lists stay visually identical and no new CSS is needed.
                    return (
                      <li
                        {...reorder.row(
                          r.id,
                          mine.map((item) => item.id),
                        )}
                        key={r.id}
                        role="treeitem"
                        aria-selected={isSelected}
                      >
                        <div
                          className={`folder-tree-row folder-tree-row--leaf${
                            isSelected ? ' folder-tree-row--selected' : ''
                          }`}
                          style={{ paddingLeft: '24px' }}
                        >
                          <button
                            type="button"
                            className="folder-tree-label folder-tree-label--template"
                            onClick={() => onSelectReport(r.id)}
                            title={r.name}
                          >
                            <span className="folder-tree-icon folder-tree-icon--template">
                              <ReportLeafIcon />
                            </span>
                            <span className="folder-tree-name">{r.name}</span>
                          </button>
                          {items.length > 1 && <RowMenu items={items} />}
                        </div>
                      </li>
                    );
                  })
                )}
              </ul>
              <Pager page={minePage} pages={minePages} onPage={setMinePage} />
            </>
          )}
        </>
      )}
    </div>
  );
}

function FolderHeader({
  onOpenList,
  label,
  count,
  open,
  onToggle,
}: {
  onOpenList?: () => void;
  label: string;
  count: number;
  open: boolean;
  onToggle(): void;
}): JSX.Element {
  return (
    <div className="folder-tree-row folder-tree-row--root" style={ROW_RESET}>
      <button
        type="button"
        className="section-chevron"
        aria-label={`${open ? 'Collapse' : 'Expand'} ${label}`}
        aria-expanded={open}
        onClick={onToggle}
      >
        <Chevron />
      </button>
      <button type="button" className="section-label" onClick={onOpenList ?? onToggle}>
        <span className="folder-tree-icon" aria-hidden="true">
          <FolderIcon />
        </span>
        <span className="folder-tree-name">{label}</span>
        <span className="folder-tree-count">{count}</span>
      </button>
    </div>
  );
}

function EmptyRow({ text }: { text: string }): JSX.Element {
  return (
    <li>
      <div className="text-dim text-sm" style={{ padding: '6px 10px 8px 24px' }}>
        {text}
      </div>
    </li>
  );
}

const ROW_RESET: CSSProperties = {
  width: '100%',
  background: 'transparent',
  border: 'none',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
};
